/**
 * ElecBid — Offline video upload queue (IndexedDB)
 *
 * When a site-walkthrough video can't reach Supabase Storage (no service, or a
 * failed upload), we stash the raw File blob here so a page refresh or app
 * switch doesn't lose the recording. The browser tab can be evicted; IndexedDB
 * survives. When connectivity returns, SmartAssessment drains the queue.
 *
 * Each record: { id, assessmentId, video, blob }
 *   - id          video id (also the IndexedDB key)
 *   - assessmentId owning assessment row
 *   - video       the lightweight metadata object stored on the assessment
 *   - blob        the actual File/Blob to upload
 */

const DB_NAME = "elecbid-video-queue";
const STORE = "videos";
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    try {
      result = fn(store);
    } catch (e) {
      reject(e);
      return;
    }
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

/** Stash a video blob + metadata for later upload. */
export async function queueVideo({ id, assessmentId, video, blob }) {
  const db = await openDB();
  try {
    await tx(db, "readwrite", (store) => store.put({ id, assessmentId, video, blob }));
  } finally {
    db.close();
  }
}

/** All queued records (includes blobs). Optionally filter by assessmentId. */
export async function listQueued(assessmentId) {
  let db;
  try {
    db = await openDB();
  } catch {
    return []; // IndexedDB unavailable — treat as empty queue
  }
  try {
    const all = await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, "readonly");
      const req = t.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    return assessmentId ? all.filter((r) => r.assessmentId === assessmentId) : all;
  } finally {
    db.close();
  }
}

/** Remove a queued record once it has uploaded successfully. */
export async function removeQueued(id) {
  let db;
  try {
    db = await openDB();
  } catch {
    return;
  }
  try {
    await tx(db, "readwrite", (store) => store.delete(id));
  } finally {
    db.close();
  }
}
